type StatusInfo = {
  status: boolean;
  /** Requisição aceita, e-mail ainda na fila (UI amarela). */
  queued?: boolean;
  unit: string;
  mail: string;
  message: string;
};

function isEnfileiradoStatus(s: any): boolean {
  if (typeof s !== 'string') return false;
  const t = s.trim().toLowerCase();
  return t === 'enfileirado' || t.startsWith('enfileirad');
}

function resolveQueued(
  item: { status?: any; queued?: boolean },
  root: { queued?: boolean } | undefined
): boolean {
  if (root?.queued === true) return true;
  if (item?.queued === true) return true;
  return isEnfileiradoStatus(item?.status);
}

function toStringSafe(value: any, fallback = '-'): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function toMailString(value: any, fallback = ''): string {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || fallback;
  return toStringSafe(value, fallback);
}

/** Texto exibido no tooltip no lugar da mensagem bruta de fila do backend. */
const MSG_ENFILEIRADO_FILA = /enfileirado;\s*aguardando processamento na fila\.?/i;

function userFacingMessage(msg: string): string {
  const t = msg.trim();
  if (MSG_ENFILEIRADO_FILA.test(t)) return 'em processamento';
  return msg;
}

/** String API statuses must not use Boolean(str) (e.g. "falha" is still truthy in JS). */
function resolveStatusForUi(raw: any, root?: { queued?: boolean }): boolean {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (!s) return root?.queued === true;
    if (s.startsWith('erro') || s.startsWith('falha') || s.startsWith('error') || s.startsWith('fail')) {
      return false;
    }
    return true;
  }
  if (raw == null) return root?.queued === true;
  if (typeof raw === 'number' && raw === 0) return root?.queued === true;
  return Boolean(raw);
}

function normalizeSingle(item: any, root?: { message?: string; queued?: boolean }): StatusInfo {
  // Legacy shape already used in the UI: { status, unit, mail, message } (boolean, ou status string vindo da API)
  if (item && typeof item === 'object' && 'status' in item && 'unit' in item && 'mail' in item) {
    return {
      status: resolveStatusForUi(item?.status, root),
      queued: resolveQueued(item, root),
      unit: toStringSafe(item.unit),
      mail: toStringSafe(item.mail),
      message: userFacingMessage(toStringSafe(item.message, '')),
    };
  }

  // New backend: { itens|items: [{ unidade, email, mensagem|message, status, ... }], jobCount, message, queued }
  if (item && typeof item === 'object' && 'unidade' in item) {
    const messageRaw =
      toStringSafe(item.mensagem, '') ||
      toStringSafe(item.message, '') ||
      toStringSafe(root?.message, '');
    const ok = resolveStatusForUi(item?.status, root);
    return {
      status: ok,
      queued: resolveQueued(item, root),
      unit: toStringSafe(item.unidade?.name) || toStringSafe(item.unidade) || '-',
      mail: toMailString(item.email) || toMailString(item?.mail) || '-',
      message: userFacingMessage(messageRaw),
    };
  }

  const status = resolveStatusForUi(item?.status ?? item?.logEntry?.status, root);
  const message = userFacingMessage(
    toStringSafe(item?.mensagem ?? item?.message ?? item?.logEntry?.message, '') || toStringSafe(root?.message, '')
  );

  const unit =
    toStringSafe(item?.customerNotification?.metadata?.unit, '') ||
    toStringSafe(item?.logEntry?.data?.unit?.name, '') ||
    toStringSafe(item?.logEntry?.unit?.name, '') ||
    '-';

  const mail =
    toMailString(item?.mail) ||
    toMailString(item?.customerNotification?.recipient?.email) ||
    toMailString(item?.logEntry?.data?.to) ||
    toMailString(item?.logEntry?.data?.mails?.map((m: any) => m?.email));

  return {
    status,
    queued: resolveQueued(
      { status: item?.status ?? item?.logEntry?.status },
      root
    ),
    unit,
    mail: mail || '-',
    message,
  };
}

/**
 * `POST mailers/send` used to return an array of `{status, unit, mail, message}`.
 * Backend may also return `{ itens|items: [{ unidade, email, mensagem, status }], message, queued, jobCount }`
 * or a single object with `{customerNotification, logEntry, status, message, ...}`.
 * This adapter keeps the existing UI (`StatusBox`) working with those shapes.
 */
export default function normalizeSendResponseToInfos(payload: any): StatusInfo[] {
  if (Array.isArray(payload)) return payload.map((i) => normalizeSingle(i));
  if (payload && typeof payload === 'object') {
    const list = (payload as any).itens ?? (payload as any).items;
    if (Array.isArray(list)) {
      return list.map((i: any) => normalizeSingle(i, payload));
    }
  }
  if (payload && typeof payload === 'object') {
    return [normalizeSingle(payload, payload)];
  }
  return [];
}

